import React from "react";
import moment from "moment";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from "reactstrap";
import {
  FormModal,
  FormGrid,
  Field,
  DateField,
  TextField,
  SelectField,
  FormAlert,
  FormActions,
} from "../../components/Form/FormKit";
import ButtonLoader from "../../components/ButtonLoader/ButtonLoader";
import strings from "../../core/translate";
import {
  ADDABLE_ITEM_TYPES,
  ItemType,
  itemTypeMeta,
  money,
  toLocalInputValue,
} from "../../shared/work-orders";
import {
  deliverySlots,
  firstWorkingDayFrom,
  formatSlot,
  isDuringBreak,
  isWorkingDay,
  parseSchedule,
  slotDate,
} from "../../shared/branch-schedule";

/**
 * The dialogs of the work order run, kept out of the screen itself: adding and
 * editing an item, promising a delivery date, handing the job to someone,
 * talking to the client, and the confirmations that guard a step.
 *
 * Each one owns only the draft being edited. What happens to it is the
 * screen's business — they hand a plain object back through `onSubmit`.
 */

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MINUTES_PER_DAY = 24 * 60;

const startOfDay = (date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

/**
 * Add or edit one item. The type is only asked for on a top-level item — a
 * sub-item or a choice option inherits its parent's, exactly as the app does
 * it, so a "priority" repair can't hide an "optional" child.
 */
/**
 * A money field's label, named with the currency the order is priced in so
 * whoever is typing knows what they are typing — "Estimated Cost (AED)".
 * Falls back to the bare label for orders with no currency recorded.
 */
const withCurrency = (label, symbol) => {
  const sign = `${symbol ?? ""}`.trim();
  return sign ? `${label} (${sign})` : label;
};

export class ItemFormModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = this.draftFrom(props);
  }

  componentDidUpdate(prev) {
    // Reopened for a different item (or a different parent): start over.
    if (
      prev.isOpen !== this.props.isOpen ||
      prev.item !== this.props.item ||
      prev.parentId !== this.props.parentId
    ) {
      if (this.props.isOpen) this.setState(this.draftFrom(this.props));
    }
  }

  draftFrom({ item, defaultType }) {
    return {
      name: item?.name ?? "",
      description: item?.description ?? "",
      estimatedCost:
        item?.estimatedCost === null || item?.estimatedCost === undefined
          ? ""
          : `${item.estimatedCost}`,
      costPrice:
        item?.costPrice === null || item?.costPrice === undefined
          ? ""
          : `${item.costPrice}`,
      type: item?.type ?? defaultType ?? ItemType.OPTIONAL,
      hoursNeeded:
        item?.hoursNeeded === null || item?.hoursNeeded === undefined
          ? ""
          : `${item.hoursNeeded}`,
      warrantyMonths: item?.warrantyMonths ?? "",
      touched: false,
    };
  }

  /**
   * A task is a top-level item. It carries how long it takes — set by whoever
   * prices the order, as in the app.
   */
  isTask() {
    const { item, parentId, isOption } = this.props;
    return item ? !item.parentId : !parentId && !isOption;
  }

  submit = (event) => {
    event.preventDefault();
    const { name, description, estimatedCost, type } = this.state;
    if (!name.trim()) {
      this.setState({ touched: true });
      return;
    }
    // The api only accepts a positive cost, so a blank or zero price means
    // "no cost given" rather than a rejected save.
    const cost = parseFloat(estimatedCost);
    const costPrice = parseFloat(this.state.costPrice);
    const hours = parseFloat(`${this.state.hoursNeeded}`.replace(",", "."));
    this.props.onSubmit({
      name: name.trim(),
      description: description.trim(),
      estimatedCost: Number.isNaN(cost) || cost <= 0 ? null : cost,
      costPrice: Number.isNaN(costPrice) || costPrice <= 0 ? null : costPrice,
      type,
      // null takes the hours or warranty away. No mechanic is sent: the final
      // quote picks who does the job, so an item's mechanic is left as it was.
      // A warranty goes with whatever is priced, sub-items and options too.
      task: {
        ...(this.isTask() && {
          hoursNeeded: Number.isNaN(hours) || hours < 0 ? null : hours,
        }),
        warrantyMonths: this.state.warrantyMonths
          ? Number(this.state.warrantyMonths)
          : null,
      },
    });
  };

  renderTaskFields() {
    const { hoursNeeded, warrantyMonths } = this.state;
    const warrantyOptions = [
      { value: "", label: strings.no_warranty },
      ...Array.from({ length: 36 }, (_, index) => ({
        value: index + 1,
        label: strings.formatString(strings.months_count, index + 1),
      })),
    ];

    const isTask = this.isTask();

    return (
      <FormGrid>
        {isTask && (
          <TextField
            label={strings.hours_needed}
            type="number"
            min="0"
            step="0.25"
            value={hoursNeeded}
            onChange={(value) => this.setState({ hoursNeeded: value })}
          />
        )}
        <SelectField
          label={strings.warranty}
          value={warrantyMonths}
          options={warrantyOptions}
          onChange={(value) => this.setState({ warrantyMonths: value })}
        />
      </FormGrid>
    );
  }

  render() {
    const { isOpen, onClose, item, parent, parentId, isOption, saving, error } =
      this.props;
    const { name, description, estimatedCost, type, touched } = this.state;
    const isEdit = !!item;
    const title = isEdit
      ? strings.edit_item
      : isOption
      ? strings.add_choice_option
      : parentId
      ? strings.add_sub_item
      : strings.add_item;
    // A sub-item or choice option is meaningless without the finding it hangs
    // off, so that finding stays in view for as long as the popup is open
    // rather than only at the moment it was clicked from.
    const subtitle = parent
      ? isOption
        ? `${strings.parent_item}: ${parent.name} — ${strings.client_picks_one}`
        : `${strings.parent_item}: ${parent.name}`
      : isOption
      ? strings.client_picks_one
      : undefined;

    return (
      <FormModal
        isOpen={isOpen}
        title={title}
        subtitle={subtitle}
        icon={itemTypeMeta(type).icon}
        size="md"
        onClose={onClose}
      >
        <form onSubmit={this.submit}>
          <FormAlert>{error}</FormAlert>
          {/* Sub-items and options inherit the type of the item they hang off.
              For everything else the type leads the form: it is the first
              thing the worker decides about a finding, and buried under the
              description it went unnoticed and every item stayed optional. */}
          {!parentId && !isOption && (
            <FormGrid>
              <SelectField
                label={strings.item_type}
                value={type}
                // A legacy "service" item keeps its own type on the list, so
                // editing one does not silently refile it as a finding.
                options={(ADDABLE_ITEM_TYPES.includes(type)
                  ? ADDABLE_ITEM_TYPES
                  : [...ADDABLE_ITEM_TYPES, type]
                ).map((value) => ({ value, label: itemTypeMeta(value).label }))}
                onChange={(value) => this.setState({ type: value })}
              />
            </FormGrid>
          )}
          <FormGrid>
            <TextField
              label={isOption ? strings.brand_name : strings.service_part_name}
              required
              value={name}
              error={touched && !name.trim() ? strings.item_name_required : ""}
              onChange={(value) => this.setState({ name: value })}
            />
          </FormGrid>
          {/* What the client pays, next to what it costs the garage — the
              cost price is only ever shown to the Owner and Managers. */}
          <FormGrid>
            <TextField
              label={withCurrency(
                strings.selling_price,
                this.props.currencySymbol
              )}
              type="number"
              min="0"
              step="0.01"
              value={estimatedCost}
              onChange={(value) => this.setState({ estimatedCost: value })}
            />
            <TextField
              label={withCurrency(strings.cost_price, this.props.currencySymbol)}
              type="number"
              min="0"
              step="0.01"
              value={this.state.costPrice}
              onChange={(value) => this.setState({ costPrice: value })}
            />
          </FormGrid>
          <FormGrid full>
            <TextField
              label={strings.description}
              type="textarea"
              value={description}
              onChange={(value) => this.setState({ description: value })}
            />
          </FormGrid>
          {this.renderTaskFields()}
          <FormActions onCancel={onClose} loading={saving} />
        </form>
      </FormModal>
    );
  }
}

/**
 * The customer accepted the quote: the Owner or a Manager approves the job for
 * the mechanics, writing the parts it needs. Approving again replaces these
 * lines; the parts the mechanics asked for themselves stay on the list.
 */
export class ApproveWorkModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = this.draftFrom(props);
  }

  componentDidUpdate(prev) {
    if (prev.isOpen !== this.props.isOpen && this.props.isOpen) {
      this.setState(this.draftFrom(this.props));
    }
  }

  draftFrom({ parts = [] }) {
    const own = parts
      .filter((part) => !part.requestedById)
      .map((part) => ({ name: part.name ?? "", quantity: `${part.quantity ?? 1}` }));
    return { rows: own.length > 0 ? own : [{ name: "", quantity: "1" }] };
  }

  setRow = (index, patch) =>
    this.setState((state) => ({
      rows: state.rows.map((row, i) => (i === index ? { ...row, ...patch } : row)),
    }));

  removeRow = (index) =>
    this.setState((state) => ({ rows: state.rows.filter((_, i) => i !== index) }));

  addRow = () =>
    this.setState((state) => ({
      rows: [...state.rows, { name: "", quantity: "1" }],
    }));

  submit = (event) => {
    event.preventDefault();
    this.props.onSubmit(
      this.state.rows
        .filter((row) => row.name.trim())
        .map((row) => ({
          name: row.name.trim(),
          quantity: Math.max(1, parseInt(row.quantity, 10) || 1),
        }))
    );
  };

  render() {
    const { isOpen, onClose, saving, error } = this.props;
    const { rows } = this.state;

    return (
      <FormModal
        isOpen={isOpen}
        title={strings.approve_for_work}
        subtitle={strings.approve_for_work_hint}
        icon="nc-icon nc-box"
        size="md"
        onClose={onClose}
      >
        <form onSubmit={this.submit}>
          <FormAlert>{error}</FormAlert>
          <div className="wo-parts-form">
            {rows.map((row, index) => (
              <div className="wo-parts-form__row" key={index}>
                <input
                  className="form-control"
                  placeholder={strings.part_name}
                  aria-label={strings.part_name}
                  value={row.name}
                  onChange={(e) => this.setRow(index, { name: e.target.value })}
                />
                <input
                  className="form-control wo-parts-form__qty"
                  type="number"
                  min="1"
                  placeholder={strings.part_qty}
                  aria-label={strings.part_qty}
                  value={row.quantity}
                  onChange={(e) => this.setRow(index, { quantity: e.target.value })}
                />
                <button
                  type="button"
                  className="wo-act is-danger"
                  title={strings.remove_part}
                  aria-label={strings.remove_part}
                  onClick={() => this.removeRow(index)}
                >
                  <i className="nc-icon nc-simple-remove" />
                </button>
              </div>
            ))}
            <button type="button" className="wo-link" onClick={this.addRow}>
              <i className="nc-icon nc-simple-add" /> {strings.add_part}
            </button>
          </div>
          <FormActions
            onCancel={onClose}
            loading={saving}
            saveLabel={strings.approve_for_work}
          />
        </form>
      </FormModal>
    );
  }
}

/**
 * The handover: how the car leaves — one of the delivery methods, the same
 * choices as on arrival — with the driver or the recovery company when the
 * method asks for one. Mirrors the worker app's "Car to be collected by".
 *
 * `methods` are `{ value, label, details }`, details being none, person or
 * company (see DeliveryMethodForm).
 */
export class CollectionModal extends React.Component {
  state = { methodId: "", name: "", phone: "", errors: {} };

  componentDidUpdate(prev) {
    if (prev.isOpen !== this.props.isOpen && this.props.isOpen) {
      this.setState({ methodId: "", name: "", phone: "", errors: {} });
    }
  }

  get details() {
    const method = (this.props.methods ?? []).find(
      (one) => one.value === this.state.methodId
    );
    return (method && method.details) || "none";
  }

  submit = (event) => {
    event.preventDefault();
    const { methodId, name, phone } = this.state;
    const details = this.details;
    const errors = {};
    if (!methodId) errors.methodId = strings.required_field;
    if (details !== "none" && !name.trim()) errors.name = strings.required_field;
    if (details === "person" && !phone.trim()) errors.phone = strings.required_field;
    this.setState({ errors });
    if (Object.keys(errors).length > 0) return;

    this.props.onSubmit({
      collectionMethodId: methodId,
      ...(details !== "none" && { collectionContactName: name.trim() }),
      ...(details === "person" && { collectionContactPhone: phone.trim() }),
    });
  };

  render() {
    const { isOpen, onClose, saving, error, methods = [], arrivedBy } = this.props;
    const { methodId, name, phone, errors } = this.state;
    const details = this.details;

    return (
      <FormModal
        isOpen={isOpen}
        title={strings.how_is_car_leaving}
        subtitle={
          arrivedBy
            ? strings.formatString(strings.arrived_by, arrivedBy)
            : strings.collection_method_hint
        }
        icon="nc-icon nc-delivery-fast"
        size="md"
        onClose={onClose}
      >
        <form onSubmit={this.submit}>
          <FormAlert>{error}</FormAlert>
          <FormGrid>
            <SelectField
              label={strings.delivery_method}
              required
              value={methodId}
              options={methods}
              onChange={(value) =>
                this.setState({ methodId: value, errors: {} })
              }
              help={
                methods.length === 0
                  ? strings.no_delivery_methods_hint
                  : strings.collection_method_hint
              }
              error={errors.methodId}
              disabled={methods.length === 0}
            />
          </FormGrid>
          {details !== "none" && (
            <FormGrid>
              <TextField
                label={
                  details === "person"
                    ? strings.driver_name
                    : strings.recovery_company
                }
                required
                value={name}
                onChange={(value) => this.setState({ name: value })}
                error={errors.name}
              />
              {details === "person" && (
                <TextField
                  label={strings.driver_phone}
                  required
                  value={phone}
                  onChange={(value) => this.setState({ phone: value })}
                  error={errors.phone}
                />
              )}
            </FormGrid>
          )}
          <FormActions
            onCancel={onClose}
            loading={saving}
            saveLabel={strings.mark_delivered}
          />
        </form>
      </FormModal>
    );
  }
}

/**
 * What the garage charges for the work itself, asked for as the final quote
 * goes out. This is the whole of the labour on the invoice, so the quote does
 * not leave without it: a blank, a zero or anything that isn't a number keeps
 * the dialog open and says why.
 *
 * The report's own items stay out of it — those are findings about the
 * vehicle, priced as parts, and the client has already answered them.
 */
export class ServicesCostModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = this.draftFrom(props);
  }

  componentDidUpdate(prev) {
    if (prev.isOpen !== this.props.isOpen && this.props.isOpen) {
      this.setState(this.draftFrom(this.props));
    }
  }

  draftFrom({ value }) {
    return {
      value: value === null || value === undefined ? "" : `${value}`,
      touched: false,
    };
  }

  // Blank, zero and negative all fail: the api takes a positive number, and a
  // free job is not something the quote flow is for.
  error() {
    const { value } = this.state;
    if (!value.trim()) return strings.services_cost_required;
    const cost = parseFloat(value);
    if (Number.isNaN(cost) || cost <= 0) return strings.services_cost_positive;
    return "";
  }

  submit = (event) => {
    event.preventDefault();
    if (this.error()) {
      this.setState({ touched: true });
      return;
    }
    this.props.onSubmit(parseFloat(this.state.value));
  };

  render() {
    const { isOpen, onClose, saving, error } = this.props;
    const { value, touched } = this.state;

    return (
      <FormModal
        isOpen={isOpen}
        title={strings.services}
        subtitle={strings.services_cost_hint}
        icon="nc-icon nc-settings-gear-65"
        size="sm"
        onClose={onClose}
      >
        <form onSubmit={this.submit}>
          <FormAlert>{error}</FormAlert>
          <FormGrid full>
            <TextField
              label={withCurrency(
                strings.services_cost,
                this.props.currencySymbol
              )}
              type="number"
              min="0"
              step="0.01"
              required
              value={value}
              error={touched ? this.error() : ""}
              onChange={(next) => this.setState({ value: next })}
            />
          </FormGrid>
          <FormActions
            onCancel={onClose}
            loading={saving}
            saveLabel={strings.next}
            disabled={Boolean(this.error())}
          />
        </form>
      </FormModal>
    );
  }
}

/**
 * Who the job belongs to. A multi-select because a job can need more than one
 * pair of hands, and it starts from whoever is already on it so adding a
 * second mechanic doesn't drop the first.
 */
export class AssigneeModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = { selected: props.selected ?? [] };
  }

  componentDidUpdate(prev) {
    if (prev.isOpen !== this.props.isOpen && this.props.isOpen) {
      this.setState({ selected: this.props.selected ?? [] });
    }
  }

  toggle = (id) => {
    this.setState((state) => ({
      selected: state.selected.includes(id)
        ? state.selected.filter((one) => one !== id)
        : [...state.selected, id],
    }));
  };

  render() {
    const { isOpen, onClose, onSubmit, workers = [], allowLater, saving } =
      this.props;
    const { selected } = this.state;

    return (
      <Modal isOpen={isOpen} toggle={onClose} className="mmodal">
        <ModalHeader toggle={onClose}>{strings.assign_workers}</ModalHeader>
        <ModalBody>
          <p className="wo-hint">{strings.assign_workers_hint}</p>
          {workers.length === 0 && (
            <div className="wo-empty">
              <i className="nc-icon nc-single-02" />
              {strings.no_workers_to_assign}
            </div>
          )}
          {workers.map((worker) => {
            const on = selected.includes(worker.id);
            return (
              <label
                key={worker.id}
                className={`wo-worker${on ? " is-on" : ""}`}
              >
                <input
                  type="checkbox"
                  checked={on}
                  onChange={() => this.toggle(worker.id)}
                />
                <span>
                  <span className="wo-worker__name">{worker.name}</span>
                  {worker.title && (
                    <span className="wo-worker__title">{worker.title}</span>
                  )}
                </span>
              </label>
            );
          })}
        </ModalBody>
        <ModalFooter>
          {/* The quote can go out unassigned; the work itself cannot start. */}
          {allowLater && (
            <Button color="default" onClick={() => onSubmit([])}>
              {strings.assign_later}
            </Button>
          )}
          <Button color="secondary" onClick={onClose}>
            {strings.cancel}
          </Button>
          <ButtonLoader
            color="primary"
            loading={saving}
            disabled={selected.length === 0}
            onClick={() => onSubmit(selected)}
          >
            {strings.save}
          </ButtonLoader>
        </ModalFooter>
      </Modal>
    );
  }
}

/**
 * Items picked from the garage's own price list instead of typed by hand: the
 * services on the left, the sub-services of the open one on the right, each
 * ticked one becoming an item with its name and price. Ticks survive switching
 * service, so one pass can take from several. The items stay ordinary items
 * afterwards — price and description are still edited per car.
 */
export class ServicePickerModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = this.draftFrom();
  }

  componentDidUpdate(prev) {
    if (prev.isOpen !== this.props.isOpen && this.props.isOpen) {
      this.setState(this.draftFrom());
    }
    // The list arrives after the dialog opens; show its first service.
    if (prev.services !== this.props.services && !this.state.openId) {
      this.setState({ openId: this.props.services?.[0]?.id ?? null });
    }
  }

  draftFrom() {
    return {
      openId: this.props.services?.[0]?.id ?? null,
      // detail id -> detail, in the order ticked
      picked: {},
      type: ItemType.OPTIONAL,
    };
  }

  toggle = (detail) => {
    this.setState((state) => {
      const picked = { ...state.picked };
      if (picked[detail.id]) delete picked[detail.id];
      else picked[detail.id] = detail;
      return { picked };
    });
  };

  render() {
    const { isOpen, onClose, onSubmit, services, currencySymbol, saving, error } =
      this.props;
    const { openId, picked, type } = this.state;
    const chosen = Object.values(picked);
    const open = (services ?? []).find((s) => s.id === openId);

    return (
      <Modal isOpen={isOpen} toggle={onClose} className="mmodal" size="lg">
        <ModalHeader toggle={onClose}>{strings.from_garage_services}</ModalHeader>
        <ModalBody>
          {error && <div className="alert alert-danger">{error}</div>}
          {services === null ? (
            <div className="wo-empty">{strings.loading}</div>
          ) : services.length === 0 ? (
            <div className="wo-empty">
              <i className="nc-icon nc-settings-gear-65" />
              {strings.no_garage_services}
            </div>
          ) : (
            <div className="wo-picker">
              <div className="wo-picker__services">
                {services.map((service) => {
                  const ticked = (service.details ?? []).filter(
                    (d) => picked[d.id]
                  ).length;
                  return (
                    <button
                      type="button"
                      key={service.id}
                      className={`wo-picker__service${
                        service.id === openId ? " is-on" : ""
                      }`}
                      onClick={() => this.setState({ openId: service.id })}
                    >
                      <span>{service.name}</span>
                      {ticked > 0 && (
                        <span className="wo-picker__count">{ticked}</span>
                      )}
                    </button>
                  );
                })}
              </div>
              <div className="wo-picker__details">
                {(open?.details ?? []).length === 0 && (
                  <div className="wo-empty">{strings.no_sub_services}</div>
                )}
                {(open?.details ?? []).map((detail) => {
                  const on = Boolean(picked[detail.id]);
                  return (
                    <label
                      key={detail.id}
                      className={`wo-worker${on ? " is-on" : ""}`}
                    >
                      <input
                        type="checkbox"
                        checked={on}
                        onChange={() => this.toggle(detail)}
                      />
                      <span className="wo-picker__detail">
                        <span className="wo-worker__name">{detail.name}</span>
                        {detail.remark && (
                          <span className="wo-worker__title">{detail.remark}</span>
                        )}
                      </span>
                      <span className="wo-picker__price">
                        {detail.price === null || detail.price === undefined
                          ? strings.no_price_set
                          : money(detail.price, currencySymbol)}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          {chosen.length > 0 && (
            <div className="wo-picker__type">
              <span>{strings.item_type}</span>
              <select
                className="form-control"
                value={type}
                onChange={(e) => this.setState({ type: e.target.value })}
              >
                {ADDABLE_ITEM_TYPES.map((value) => (
                  <option key={value} value={value}>
                    {itemTypeMeta(value).label}
                  </option>
                ))}
              </select>
            </div>
          )}
          <Button color="secondary" onClick={onClose}>
            {strings.cancel}
          </Button>
          <ButtonLoader
            color="primary"
            loading={saving}
            disabled={chosen.length === 0}
            onClick={() => onSubmit({ details: chosen, type })}
          >
            {`${strings.add_selected} (${chosen.length})`}
          </ButtonLoader>
        </ModalFooter>
      </Modal>
    );
  }
}

/**
 * The date the car is promised back, held inside the garage's working week the
 * way the app holds it: days the garage is shut cannot be picked at all, and
 * the time comes from the garage's own slots rather than a free clock. Closed
 * hours are never offered; the break and times already gone by are shown
 * greyed, so it is clear why they are not on offer.
 *
 * The backend still refuses anything outside the window with its own wording.
 * This is what stops the user reaching that refusal.
 *
 * A garage with no appointment settings publishes no schedule. Nothing holds
 * the choice inside then, so the plain picker comes back — the same fallback
 * the app makes.
 */
export class DeliveryModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = this.draftFrom(props);
  }

  componentDidUpdate(prev) {
    if (prev.isOpen !== this.props.isOpen && this.props.isOpen) {
      this.setState(this.draftFrom(this.props));
    }
  }

  // Editing an existing promise reopens on the day and slot it was made for.
  draftFrom({ value, schedule }) {
    const at = value ? new Date(value) : null;
    const known = at && !Number.isNaN(at.getTime());
    return {
      value: toLocalInputValue(value),
      day: known ? startOfDay(at) : null,
      minutes: known ? at.getHours() * 60 + at.getMinutes() : null,
      touched: false,
      parsed: parseSchedule(schedule),
    };
  }

  // Only a day the garage opens, and never one already gone.
  isSelectableDay = (current) => {
    const day = current.toDate();
    return day >= startOfDay(new Date()) && isWorkingDay(this.state.parsed, day);
  };

  pickDay = (picked) => {
    if (!picked || typeof picked.toDate !== "function") return;
    const day = startOfDay(picked.toDate());
    // A slot open on one day can be in the past on another, so the time is
    // dropped rather than carried over to a day it no longer suits.
    this.setState((prev) => ({
      day,
      minutes: this.slotIsOpen(prev.parsed, day, prev.minutes) ? prev.minutes : null,
    }));
  };

  slotIsOpen = (parsed, day, minutes) => {
    if (!parsed || !day || minutes === null || minutes === undefined) return false;
    if (isDuringBreak(parsed, minutes % MINUTES_PER_DAY)) return false;
    return slotDate(day, minutes) > new Date();
  };

  submit = (event) => {
    event.preventDefault();
    const { parsed, day, minutes, value } = this.state;

    if (!parsed) {
      if (!value) return this.setState({ touched: true });
      return this.props.onSubmit(value);
    }
    if (!day || minutes === null) return this.setState({ touched: true });
    return this.props.onSubmit(toLocalInputValue(slotDate(day, minutes)));
  };

  renderSlots() {
    const { parsed, day, minutes } = this.state;

    return (
      <div className="delivery-slots">
        {deliverySlots(parsed).map((slot) => {
          const onBreak = isDuringBreak(parsed, slot % MINUTES_PER_DAY);
          return (
            <button
              key={slot}
              type="button"
              disabled={!this.slotIsOpen(parsed, day, slot)}
              title={onBreak ? strings.garage_break : undefined}
              className={`delivery-slot${minutes === slot ? " is-picked" : ""}${
                onBreak ? " is-break" : ""
              }`}
              onClick={() => this.setState({ minutes: slot })}
            >
              {formatSlot(slot)}
            </button>
          );
        })}
      </div>
    );
  }

  render() {
    const { isOpen, onClose, saving, error, title, saveLabel } = this.props;
    const { value, day, minutes, touched, parsed } = this.state;
    // The api publishes ISO weekdays, where Monday is 1. The names are a
    // zero-based list, so the index runs one behind.
    const days = (parsed?.workingWeekdays ?? [])
      .map((weekday) => WEEKDAYS[weekday - 1])
      .filter(Boolean)
      .join(", ");
    const hint = parsed
      ? `${strings.garage_hours}: ${parsed.workingHours}${
          days ? ` (${days})` : ""
        }${parsed.breakTime ? ` · ${strings.garage_break}: ${parsed.breakTime}` : ""}`
      : undefined;

    return (
      <FormModal
        isOpen={isOpen}
        title={title || strings.delivery_date}
        icon="nc-icon nc-calendar-60"
        size="md"
        onClose={onClose}
      >
        <form onSubmit={this.submit}>
          <FormAlert>{error}</FormAlert>
          <FormGrid full>
            {parsed ? (
              <React.Fragment>
                <DateField
                  label={strings.delivery_day}
                  required
                  value={day ? moment(day) : ""}
                  viewDate={moment(firstWorkingDayFrom(parsed, day || new Date()))}
                  isValidDate={this.isSelectableDay}
                  error={touched && !day ? strings.delivery_required : ""}
                  help={hint}
                  onChange={this.pickDay}
                />
                <Field
                  label={strings.delivery_time}
                  required
                  error={
                    touched && day && minutes === null
                      ? strings.delivery_time_required
                      : ""
                  }
                >
                  {day ? (
                    this.renderSlots()
                  ) : (
                    <p className="delivery-slots__empty">
                      {strings.delivery_pick_day_first}
                    </p>
                  )}
                </Field>
              </React.Fragment>
            ) : (
              <TextField
                label={strings.delivery_date}
                type="datetime-local"
                required
                value={value}
                error={touched && !value ? strings.delivery_required : ""}
                onChange={(next) => this.setState({ value: next })}
              />
            )}
          </FormGrid>
          <FormActions
            onCancel={onClose}
            loading={saving}
            saveLabel={saveLabel || strings.save}
          />
        </form>
      </FormModal>
    );
  }
}

/**
 * Every conversation on the screen opens here: the client's own thread, and
 * the one hanging off each finding. One panel at the edge rather than a thread
 * unfolding inside the report — that pushed every item below it down the page
 * and moved the row being read out from under the pointer.
 *
 * The draft lives with the screen, keyed by whichever thread is open, so
 * closing the panel to look at something never loses what was half typed.
 */
export class MessageDrawer extends React.Component {
  constructor(props) {
    super(props);
    this.inputRef = React.createRef();
  }

  componentDidMount() {
    document.addEventListener("keydown", this.onKey);
  }

  componentDidUpdate(prev) {
    // Opened, or switched to another item's thread: the composer is where the
    // reader is going next either way.
    const switched =
      this.props.isOpen &&
      (!prev.isOpen || prev.threadKey !== this.props.threadKey);
    if (switched && this.inputRef.current) this.inputRef.current.focus();
  }

  componentWillUnmount() {
    document.removeEventListener("keydown", this.onKey);
  }

  onKey = (event) => {
    if (event.key === "Escape" && this.props.isOpen) this.props.onClose();
  };

  submit = (event) => {
    event.preventDefault();
    this.props.onSend();
  };

  render() {
    const {
      isOpen,
      title,
      subtitle,
      messages = [],
      sending,
      draft = "",
      onDraftChange,
      onClose,
    } = this.props;
    if (!isOpen) return null;

    return (
      <>
        <div className="wo-drawer__scrim" onClick={onClose} />
        <aside
          className="wo-drawer"
          role="dialog"
          aria-modal="true"
          aria-label={title}
        >
          <div className="wo-drawer__head">
            <div>
              <div className="wo-drawer__title">{title}</div>
              {subtitle && <div className="wo-drawer__sub">{subtitle}</div>}
            </div>
            <button
              type="button"
              className="wo-act wo-drawer__close"
              title={strings.close}
              aria-label={strings.close}
              onClick={onClose}
            >
              <i className="nc-icon nc-simple-remove" />
            </button>
          </div>

          <div className="wo-drawer__body">
            <Thread messages={messages} />
          </div>

          <form className="wo-drawer__form" onSubmit={this.submit}>
            <input
              ref={this.inputRef}
              className="form-control"
              placeholder={strings.reply_placeholder}
              value={draft}
              onChange={(event) => onDraftChange(event.target.value)}
            />
            <ButtonLoader color="primary" loading={sending} type="submit">
              {strings.send}
            </ButtonLoader>
          </form>
        </aside>
      </>
    );
  }
}

/** Messages of one thread, the garage's own on the right. */
export const Thread = ({ messages = [] }) => {
  if (messages.length === 0) {
    return <div className="wo-thread__empty">{strings.no_messages}</div>;
  }
  return (
    <>
      {messages.map((message) => {
        const mine = message.sender !== "client";
        return (
          <div key={message.id} className={`wo-msg${mine ? " is-mine" : ""}`}>
            <div>
              <div className="wo-msg__body">{message.body}</div>
              <div className="wo-msg__meta">
                {mine ? strings.garage : strings.client}
              </div>
            </div>
          </div>
        );
      })}
    </>
  );
};

/** A step that is hard to walk back asks first. */
export const ConfirmModal = ({
  isOpen,
  title,
  body,
  confirmLabel,
  color = "primary",
  saving,
  onConfirm,
  onClose,
}) => (
  <Modal isOpen={isOpen} toggle={onClose} className="mmodal">
    <ModalHeader toggle={onClose}>{title}</ModalHeader>
    <ModalBody>{body}</ModalBody>
    <ModalFooter>
      <Button color="secondary" onClick={onClose}>
        {strings.cancel}
      </Button>
      <ButtonLoader color={color} loading={saving} onClick={onConfirm}>
        {confirmLabel || strings.confirm}
      </ButtonLoader>
    </ModalFooter>
  </Modal>
);
